import type {
  PlatformAccount,
  PlatformAccountCreate,
  PlatformAccountUpdate,
  ProductionOrder,
  ProductionCreate,
  ProductionOptions,
  ProductionEngineering,
  ProductionStockOption,
  ProductionAction,
  ProductionDispatch,
  ProductionSplit,
  ProductionIssue,
  ProductionReturn,
  ProductionCost,
  ProductionReceipt,
  ProductionOperationAction,
  ProductionReportCreate,
  ProductionReverse,
  ManufacturingOption,
  WorkCenter,
  WorkCenterSave,
  WorkCalendarDay,
  WorkCalendarSave,
  Bom,
  BomSave,
  Routing,
  RoutingSave,
  ApiEnvelope,
  CategoryCreateRequest,
  CategoryItem,
  CashDirection,
  CashOrder,
  CashOrderCreateRequest,
  CustomerCreateRequest,
  CustomerItem,
  DepartmentCreateRequest,
  DepartmentItem,
  EmployeeCreateRequest,
  EmployeeItem,
  FinanceAuxiliaryOptions,
  FinanceSummary,
  GeneralLedgerRow,
  LoginRequest,
  MasterDataOptions,
  OpenItem,
  OpenItemDirection,
  PackageOption,
  PageResult,
  ProductCreateRequest,
  ProductItem,
  RoleOption,
  SessionUser,
  PlatformAuditEvent,
  PlatformOperationsOverview,
  PlatformTenantSignal,
  PlatformTaskIssue,
  SettlementAccount,
  StockBalance,
  StockCheck,
  StockCheckRequest,
  StockLog,
  MiscStockMovement,
  MiscStockMovementRequest,
  StockTransfer,
  StockTransferRequest,
  AccountingPeriod,
  AccountSubject,
  AccountSet,
  SubjectWriteRequest,
  AgingBucket,
  SupplierCreateRequest,
  SupplierItem,
  TenantCreateRequest,
  TenantListItem,
  TradeExecutionDocument,
  TradeExecutionRequest,
  DirectTradeExecutionRequest,
  TradeOrder,
  TradeOrderCreateRequest,
  TradeReturnDocument,
  TradeReturnRequest,
  TradeSummary,
  TrialBalanceRow,
  UnitCreateRequest,
  UnitItem,
  WarehouseCreateRequest,
  WarehouseItem,
  Voucher,
  VoucherCreateRequest,
  WorkspaceSummary,
} from "@nimbus-erp/shared";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly traceId?: string,
    public readonly fieldErrors?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  idempotencyKey?: string;
}

const listDateParams = (startDate: string, endDate: string) => ({
  ...(startDate ? { startDate } : {}),
  ...(endDate ? { endDate } : {}),
});

export class ApiClient {
  private demoWriteProtected = false;
  private expectedTenant: string | null = null;
  setExpectedTenant(id: string | null) { this.expectedTenant=id; }

  setDemoWriteProtected(enabled: boolean) {
    this.demoWriteProtected = enabled;
  }

  backgroundTasks = (page = 0, size = 20, status = "", startDate = "", endDate = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").BackgroundTask>>(
      `/tasks?${new URLSearchParams({ page: String(page), size: String(size), status, ...listDateParams(startDate, endDate) })}`,
    );
  inventoryCountSessions = () =>
    this.request<import("@nimbus-erp/shared").InventoryCountSession[]>(
      "/inventory/count-sessions",
    );
  businessDocumentHistory = (type: string, id: string) =>
    this.request<
      Array<{
        action: string;
        reason: string | null;
        actorName: string;
        at: string;
      }>
    >(
      `/audit/documents/${encodeURIComponent(type)}/${encodeURIComponent(id)}/events`,
    );
  inventoryCountSession = (id: string) =>
    this.request<import("@nimbus-erp/shared").InventoryCountSession>(
      `/inventory/count-sessions/${id}`,
    );
  createInventoryCountSession = (
    body: import("@nimbus-erp/shared").InventoryCountCreate,
  ) =>
    this.request<import("@nimbus-erp/shared").InventoryCountSession>(
      "/inventory/count-sessions",
      { method: "POST", body },
    );
  addInventoryCountFoundItem = (
    id: string,
    body: {
      version: number;
      productId: string;
      batchNo: string;
      actualQuantity: number;
      unitCost: number;
    },
  ) =>
    this.request<import("@nimbus-erp/shared").InventoryCountSession>(
      `/inventory/count-sessions/${id}/found-items`,
      { method: "POST", body },
    );
  saveInventoryCounts = (
    id: string,
    body: {
      version: number;
      items: Array<{ itemId: string; actualQuantity: number }>;
    },
  ) =>
    this.request<import("@nimbus-erp/shared").InventoryCountSession>(
      `/inventory/count-sessions/${id}/counts`,
      { method: "PUT", body },
    );
  submitInventoryCount = (id: string, version: number, reason: string) =>
    this.request<import("@nimbus-erp/shared").InventoryCountSession>(
      `/inventory/count-sessions/${id}/submit`,
      { method: "POST", body: { version, reason } },
    );
  postInventoryCount = (id: string, version: number, reason: string) =>
    this.request<import("@nimbus-erp/shared").InventoryCountSession>(
      `/inventory/count-sessions/${id}/post`,
      {
        method: "POST",
        body: { version, reason },
        idempotencyKey: crypto.randomUUID(),
      },
    );
  cancelInventoryCount = (id: string, version: number, reason: string) =>
    this.request<import("@nimbus-erp/shared").InventoryCountSession>(
      `/inventory/count-sessions/${id}/cancel`,
      { method: "POST", body: { version, reason } },
    );
  backgroundTaskSummary = () =>
    this.request<import("@nimbus-erp/shared").BackgroundTaskSummary>(
      "/tasks/summary",
    );
  requestAlertRefreshTask = () =>
    this.request<import("@nimbus-erp/shared").BackgroundTask>(
      "/tasks/alert-refresh",
      { method: "POST" },
    );
  requestMrpTask = (
    body: { planningId: string; warehouseIds: string[] },
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").BackgroundTask>("/tasks/mrp", {
      method: "POST",
      body,
      idempotencyKey,
    });
  requestInventoryCostRecalculationTask = (
    body: import("@nimbus-erp/shared").InventoryCostRecalculationRequest,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").BackgroundTask>(
      "/tasks/cost-recalculation",
      { method: "POST", body, idempotencyKey },
    );
  requestPeriodCloseTask = (
    id: string,
    body: { action: "CLOSE"; version: number; reason: string },
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").BackgroundTask>(
      `/tasks/periods/${id}/close`,
      { method: "POST", body, idempotencyKey },
    );
  requestReportSnapshotTask = (
    body: { type: string; startDate: string; endDate: string },
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").BackgroundTask>(
      "/tasks/report-snapshots",
      { method: "POST", body, idempotencyKey },
    );
  retryBackgroundTask = (id: string, version: number) =>
    this.request<import("@nimbus-erp/shared").BackgroundTask>(
      `/tasks/${id}/retry`,
      { method: "POST", body: { version } },
    );
  cancelBackgroundTask = (id: string, version: number) =>
    this.request<import("@nimbus-erp/shared").BackgroundTask>(
      `/tasks/${id}/cancel`,
      { method: "POST", body: { version } },
    );
  analyticsOverview = (from: string, to: string) =>
    this.request<import("@nimbus-erp/shared").AnalyticsOverview>(
      `/analytics/overview?${new URLSearchParams({ from, to })}`,
    );
  alertRules = () =>
    this.request<import("@nimbus-erp/shared").AlertRule[]>("/analytics/rules");
  saveAlertRule = (
    code: string,
    body: import("@nimbus-erp/shared").AlertRuleSave,
  ) =>
    this.request<import("@nimbus-erp/shared").AlertRule>(
      `/analytics/rules/${code}`,
      { method: "PUT", body },
    );
  refreshAlerts = () =>
    this.request<import("@nimbus-erp/shared").AlertRefresh>(
      "/analytics/alerts/refresh",
      { method: "POST" },
    );
  businessAlerts = (
    page = 0,
    size = 20,
    status = "",
    rule = "",
    search = "",
    mine = false, startDate = "", endDate = "",
  ) =>
    this.request<PageResult<import("@nimbus-erp/shared").BusinessAlert>>(
      `/analytics/alerts?${new URLSearchParams({ page: String(page), size: String(size), status, rule, search, mine: String(mine),...listDateParams(startDate,endDate) })}`,
    );
  businessAlert = (id: string) =>
    this.request<import("@nimbus-erp/shared").BusinessAlert>(
      `/analytics/alerts/${id}`,
    );
  businessAlertAction = (
    id: string,
    body: import("@nimbus-erp/shared").BusinessAlertAction,
  ) =>
    this.request<import("@nimbus-erp/shared").BusinessAlert>(
      `/analytics/alerts/${id}/action`,
      { method: "POST", body },
    );
  alertAssignees = (search = "") =>
    this.request<{ id: string; name: string }[]>(
      `/analytics/assignees?${new URLSearchParams({ search })}`,
    );

  private approvalHandler:
    ((r: import("@nimbus-erp/shared").WorkflowRequest) => void) | undefined;
  customerPolicy = (id: string) =>
    this.request<import("@nimbus-erp/shared").CustomerPolicy>(
      `/trade-policies/customers/${id}`,
    );
  saveCustomerPolicy = (
    id: string,
    body: import("@nimbus-erp/shared").CustomerPolicySave,
  ) =>
    this.request<import("@nimbus-erp/shared").CustomerPolicy>(
      `/trade-policies/customers/${id}`,
      { method: "PUT", body },
    );
  productPolicy = (id: string) =>
    this.request<import("@nimbus-erp/shared").ProductPolicy>(
      `/trade-policies/products/${id}`,
    );
  saveProductPolicy = (
    id: string,
    body: import("@nimbus-erp/shared").ProductPolicySave,
  ) =>
    this.request<import("@nimbus-erp/shared").ProductPolicy>(
      `/trade-policies/products/${id}`,
      { method: "PUT", body },
    );
  customerPrices = (customerId: string) =>
    this.request<import("@nimbus-erp/shared").CustomerPrice[]>(
      `/trade-policies/prices?${new URLSearchParams({ customerId })}`,
    );
  saveCustomerPrice = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").CustomerPriceSave,
  ) =>
    this.request<import("@nimbus-erp/shared").CustomerPrice>(
      `/trade-policies/prices${id ? `/${id}` : ""}`,
      { method: id ? "PUT" : "POST", body },
    );
  customerCredit = (id: string) =>
    this.request<import("@nimbus-erp/shared").CustomerCredit>(
      `/trade-policies/credit/${id}`,
    );
  salesQuote = (
    customerId: string,
    productId: string,
    quantity: number,
    date: string,
    warehouseId?: string,
  ) =>
    this.request<import("@nimbus-erp/shared").SalesQuote>(
      `/trade-policies/quote?${new URLSearchParams({ customerId, productId, quantity: String(quantity), date, ...(warehouseId ? { warehouseId } : {}) })}`,
    );
  purchasePriceHistory = (productId: string, supplierId?: string) =>
    this.request<import("@nimbus-erp/shared").PurchasePriceHistory[]>(
      `/trade-policies/purchase-prices?${new URLSearchParams({ productId, ...(supplierId ? { supplierId } : {}) })}`,
    );
  setApprovalRequiredHandler(
    handler:
      ((r: import("@nimbus-erp/shared").WorkflowRequest) => void) | undefined,
  ) {
    this.approvalHandler = handler;
  }
  beginWorkflowApproval(request: import("@nimbus-erp/shared").WorkflowRequest) {
    this.approvalHandler?.(request);
  }
  workflowOptions = (search = "") =>
    this.request<{
      users: import("@nimbus-erp/shared").WorkflowPerson[];
      roles: import("@nimbus-erp/shared").WorkflowPerson[];
    }>(`/workflow/options?${new URLSearchParams({ search })}`);
  workflowTemplates = () =>
    this.request<import("@nimbus-erp/shared").WorkflowTemplate[]>(
      "/workflow/templates",
    );
  saveWorkflowTemplate = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").WorkflowTemplateSave,
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").WorkflowTemplate>(
      `/workflow/templates${id ? `/${id}` : ""}`,
      {
        method: id ? "PUT" : "POST",
        body,
        ...(!id ? { idempotencyKey: key } : {}),
      },
    );
  workflowTemplateAction = (
    id: string,
    body: import("@nimbus-erp/shared").WorkflowAction,
  ) =>
    this.request<import("@nimbus-erp/shared").WorkflowTemplate>(
      `/workflow/templates/${id}/action`,
      { method: "POST", body },
    );
  workflowManagers = () =>
    this.request<import("@nimbus-erp/shared").WorkflowManager[]>(
      "/workflow/managers",
    );
  saveWorkflowManager = (body: {
    userId: string;
    managerId: string;
    version: number;
  }) =>
    this.request<import("@nimbus-erp/shared").WorkflowManager[]>(
      "/workflow/managers",
      { method: "PUT", body },
    );
  previewWorkflow = (body: import("@nimbus-erp/shared").WorkflowRequest) =>
    this.request<import("@nimbus-erp/shared").WorkflowPreview>(
      "/workflow/preview",
      { method: "POST", body },
    );
  submitWorkflow = (
    request: import("@nimbus-erp/shared").WorkflowRequest,
    reason: string,
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").WorkflowInstance>(
      "/workflow/instances",
      { method: "POST", body: { request, reason }, idempotencyKey: key },
    );
  workflowInstances = (scope = "TODO", page = 0, size = 20, search = "", startDate = "", endDate = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").WorkflowInstance>>(
      `/workflow/instances?${new URLSearchParams({ scope, page: String(page), size: String(size), search, ...listDateParams(startDate, endDate) })}`,
    );
  workflowInstance = (id: string) =>
    this.request<import("@nimbus-erp/shared").WorkflowInstance>(
      `/workflow/instances/${id}`,
    );
  workflowAction = (
    id: string,
    body: import("@nimbus-erp/shared").WorkflowAction,
  ) =>
    this.request<import("@nimbus-erp/shared").WorkflowInstance>(
      `/workflow/instances/${id}/action`,
      { method: "POST", body },
    );
  markWorkflowRead = (id: string) =>
    this.request<import("@nimbus-erp/shared").WorkflowInstance>(
      `/workflow/instances/${id}/read`,
      { method: "POST" },
    );
  executeWorkflow = (id: string, version: number) =>
    this.request<import("@nimbus-erp/shared").WorkflowInstance>(
      `/workflow/instances/${id}/execute`,
      { method: "POST", body: { version } },
    );

  equipmentOptions = (search = "") =>
    this.request<import("@nimbus-erp/shared").EquipmentOptions>(
      `/equipment/options?${new URLSearchParams({ search })}`,
    );
  equipmentAssets = (page = 0, size = 20, search = "", status = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").EquipmentAsset>>(
      `/equipment/assets?${new URLSearchParams({ page: String(page), size: String(size), search, status })}`,
    );
  equipmentAsset = (id: string) =>
    this.request<import("@nimbus-erp/shared").EquipmentAsset>(
      `/equipment/assets/${id}`,
    );
  saveEquipmentAsset = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").EquipmentAssetSave,
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentAsset>(
      `/equipment/assets${id ? `/${id}` : ""}`,
      {
        method: id ? "PUT" : "POST",
        body,
        ...(!id ? { idempotencyKey: key } : {}),
      },
    );
  equipmentPlans = (page = 0, size = 20, search = "", kind = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").EquipmentPlan>>(
      `/equipment/plans?${new URLSearchParams({ page: String(page), size: String(size), search, kind })}`,
    );
  saveEquipmentPlan = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").EquipmentPlanSave,
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentPlan>(
      `/equipment/plans${id ? `/${id}` : ""}`,
      {
        method: id ? "PUT" : "POST",
        body,
        ...(!id ? { idempotencyKey: key } : {}),
      },
    );
  generateEquipmentTasks = (body: {
    from: string;
    to: string;
    equipmentId: string | null;
  }) =>
    this.request<{ created: number; existing: number }>(
      "/equipment/tasks/generate",
      { method: "POST", body },
    );
  equipmentTasks = (
    from: string,
    to: string,
    page = 0,
    size = 20,
    search = "",
    status = "",
  ) =>
    this.request<PageResult<import("@nimbus-erp/shared").EquipmentTask>>(
      `/equipment/tasks?${new URLSearchParams({ from, to, page: String(page), size: String(size), search, status })}`,
    );
  equipmentTask = (id: string) =>
    this.request<import("@nimbus-erp/shared").EquipmentTask>(
      `/equipment/tasks/${id}`,
    );
  completeEquipmentTask = (
    id: string,
    body: import("@nimbus-erp/shared").EquipmentTaskComplete,
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentTask>(
      `/equipment/tasks/${id}/complete`,
      { method: "POST", body },
    );
  equipmentTaskAction = (
    id: string,
    body: { version: number; action: "CANCEL" | "REOPEN"; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentTask>(
      `/equipment/tasks/${id}/action`,
      { method: "POST", body },
    );
  equipmentFaults = (page = 0, size = 20, search = "", status = "", startDate = "", endDate = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").EquipmentFault>>(
      `/equipment/faults?${new URLSearchParams({ page: String(page), size: String(size), search, status, ...listDateParams(startDate,endDate) })}`,
    );
  equipmentFault = (id: string) =>
    this.request<import("@nimbus-erp/shared").EquipmentFault>(
      `/equipment/faults/${id}`,
    );
  createEquipmentFault = (
    body: import("@nimbus-erp/shared").EquipmentFaultCreate,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentFault>(
      "/equipment/faults",
      { method: "POST", body, idempotencyKey },
    );
  equipmentFaultAction = (
    id: string,
    body: import("@nimbus-erp/shared").EquipmentAction,
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentFault>(
      `/equipment/faults/${id}/action`,
      { method: "POST", body },
    );
  equipmentWork = (
    id: string,
    body: {
      version: number;
      businessDate: string;
      minutes: number;
      description: string;
    },
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentFault>(
      `/equipment/faults/${id}/work`,
      { method: "POST", body, idempotencyKey },
    );
  reverseEquipmentWork = (
    id: string,
    workId: string,
    body: { version: number; businessDate: string; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentFault>(
      `/equipment/faults/${id}/work/${workId}/reverse`,
      { method: "POST", body },
    );
  equipmentSpare = (
    id: string,
    body: import("@nimbus-erp/shared").EquipmentSpare,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentFault>(
      `/equipment/faults/${id}/spares`,
      { method: "POST", body, idempotencyKey },
    );
  returnEquipmentSpare = (
    id: string,
    body: {
      version: number;
      movementId: string;
      quantity: number;
      businessDate: string;
      reason: string;
    },
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").EquipmentFault>(
      `/equipment/faults/${id}/spare-returns`,
      { method: "POST", body, idempotencyKey },
    );
  equipmentAnalysis = (from: string, to: string) =>
    this.request<import("@nimbus-erp/shared").EquipmentAnalysis>(
      `/equipment/analysis?${new URLSearchParams({ from, to })}`,
    );
  qualityStandardOptions = (search = "") =>
    this.request<import("@nimbus-erp/shared").QualityStandard[]>(
      `/quality/standard-options?${new URLSearchParams({ search })}`,
    );
  reverseQualityDisposition = (
    id: string,
    dispositionId: string,
    body: { version: number; businessDate: string; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").QualityNonconformance>(
      `/quality/nonconformances/${id}/dispositions/${dispositionId}/reverse`,
      { method: "POST", body },
    );
  qualityStandards = (page = 0, size = 20, search = "", scope = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").QualityStandard>>(
      `/quality/standards?${new URLSearchParams({ page: String(page), size: String(size), search, scope })}`,
    );
  qualityStandard = (id: string) =>
    this.request<import("@nimbus-erp/shared").QualityStandard>(
      `/quality/standards/${id}`,
    );
  saveQualityStandard = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").QualityStandardSave,
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").QualityStandard>(
      `/quality/standards${id ? `/${id}` : ""}`,
      {
        method: id ? "PUT" : "POST",
        body,
        ...(!id ? { idempotencyKey: key } : {}),
      },
    );
  qualityStandardAction = (
    id: string,
    action: "PUBLISH" | "RETIRE",
    body: { version: number; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").QualityStandard>(
      `/quality/standards/${id}/${action}`,
      { method: "POST", body },
    );
  qualitySources = (scope: string, productId: string, search = "") =>
    this.request<import("@nimbus-erp/shared").QualitySource[]>(
      `/quality/sources?${new URLSearchParams({ scope, productId, search })}`,
    );
  qualityInspections = (
    page = 0,
    size = 20,
    search = "",
    scope = "",
    status = "", startDate = "", endDate = "",
  ) =>
    this.request<PageResult<import("@nimbus-erp/shared").QualityInspection>>(
      `/quality/inspections?${new URLSearchParams({ page: String(page), size: String(size), search, scope, status, ...listDateParams(startDate, endDate) })}`,
    );
  qualityInspection = (id: string) =>
    this.request<import("@nimbus-erp/shared").QualityInspection>(
      `/quality/inspections/${id}`,
    );
  createQualityInspection = (
    body: { standardId: string; sourceId: string },
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").QualityInspection>(
      "/quality/inspections",
      { method: "POST", body, idempotencyKey },
    );
  submitQualityInspection = (
    id: string,
    body: import("@nimbus-erp/shared").QualitySubmit,
  ) =>
    this.request<import("@nimbus-erp/shared").QualityInspection>(
      `/quality/inspections/${id}/submit`,
      { method: "POST", body },
    );
  reinspectQuality = (
    id: string,
    action: "reinspect" | "withdraw",
    body: { version: number; businessDate: string; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").QualityInspection>(
      `/quality/inspections/${id}/${action}`,
      { method: "POST", body },
    );
  qualityNonconformances = (page = 0, size = 20, search = "", status = "", startDate = "", endDate = "") =>
    this.request<
      PageResult<import("@nimbus-erp/shared").QualityNonconformance>
    >(
      `/quality/nonconformances?${new URLSearchParams({ page: String(page), size: String(size), search, status, ...listDateParams(startDate, endDate) })}`,
    );
  qualityNonconformance = (id: string) =>
    this.request<import("@nimbus-erp/shared").QualityNonconformance>(
      `/quality/nonconformances/${id}`,
    );
  disposeQuality = (
    id: string,
    body: import("@nimbus-erp/shared").QualityDispose,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").QualityNonconformance>(
      `/quality/nonconformances/${id}/dispose`,
      { method: "POST", body, idempotencyKey },
    );
  qualityRework = (
    id: string,
    body: import("@nimbus-erp/shared").QualityRework,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").QualityNonconformance>(
      `/quality/nonconformances/${id}/rework`,
      { method: "POST", body, idempotencyKey },
    );
  qualityAnalysis = (from: string, to: string, scope = "") =>
    this.request<import("@nimbus-erp/shared").QualityAnalysis>(
      `/quality/analysis?${new URLSearchParams({ from, to, scope })}`,
    );
  productionReworks = (id: string) =>
    this.request<import("@nimbus-erp/shared").ReworkLink[]>(
      `/manufacturing/orders/${id}/reworks`,
    );
  createRework = (
    id: string,
    body: import("@nimbus-erp/shared").ReworkCreate,
    idempotencyKey: string,
  ) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/reworks`, {
      method: "POST",
      body,
      idempotencyKey,
    });
  completeRework = (
    id: string,
    body: {
      childVersion: number;
      parentVersion: number;
      businessDate: string;
      reason: string;
    },
    idempotencyKey: string,
  ) =>
    this.request<ProductionOrder>(
      `/manufacturing/orders/${id}/rework-completion`,
      { method: "POST", body, idempotencyKey },
    );
  reverseRework = (
    id: string,
    resultId: string,
    body: { version: number; businessDate: string; reason: string },
  ) =>
    this.request<ProductionOrder>(
      `/manufacturing/orders/${id}/rework-results/${resultId}/reverse`,
      { method: "POST", body },
    );
  extendProductionQuantity = (
    id: string,
    body: { version: number; extraQuantity: number; reason: string },
  ) =>
    this.request<ProductionOrder>(
      `/manufacturing/orders/${id}/extend-quantity`,
      { method: "POST", body },
    );
  planningSuppliers = (search = "") =>
    this.request<ManufacturingOption[]>(
      `/planning/supplier-options?${new URLSearchParams({ search })}`,
    );
  planningVersions = (
    kind: "FORECAST" | "MPS",
    page = 0,
    size = 20,
    search = "", startDate = "", endDate = "",
  ) =>
    this.request<
      import("@nimbus-erp/shared").PageResult<
        import("@nimbus-erp/shared").PlanningVersion
      >
    >(
      `/planning/versions?${new URLSearchParams({ kind, page: String(page), size: String(size), search, ...listDateParams(startDate, endDate) })}`,
    );
  planningVersion = (id: string) =>
    this.request<import("@nimbus-erp/shared").PlanningVersion>(
      `/planning/versions/${id}`,
    );
  saveForecast = (
    id: string | null,
    body: import("@nimbus-erp/shared").ForecastSave,
    idempotencyKey?: string,
  ) =>
    this.request<import("@nimbus-erp/shared").PlanningVersion>(
      `/planning/forecasts${id ? `/${id}` : ""}`,
      {
        method: id ? "PUT" : "POST",
        body,
        ...(idempotencyKey ? { idempotencyKey } : {}),
      },
    );
  createMps = (
    body: import("@nimbus-erp/shared").MpsCreate,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").PlanningVersion>(
      "/planning/mps",
      { method: "POST", body, idempotencyKey },
    );
  adjustMps = (
    id: string,
    body: {
      version: number;
      reason: string;
      lines: import("@nimbus-erp/shared").PlanningLineInput[];
    },
  ) =>
    this.request<import("@nimbus-erp/shared").PlanningVersion>(
      `/planning/mps/${id}`,
      { method: "PUT", body },
    );
  planningAction = (
    id: string,
    action: "FREEZE" | "WITHDRAW",
    body: { version: number; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").PlanningVersion>(
      `/planning/versions/${id}/${action}`,
      { method: "POST", body },
    );
  planningCompare = (before: string, after: string) =>
    this.request<import("@nimbus-erp/shared").PlanningDelta[]>(
      `/planning/compare?${new URLSearchParams({ before, after })}`,
    );
  planningPolicies = (search = "") =>
    this.request<import("@nimbus-erp/shared").PlanningPolicy[]>(
      `/planning/policies?${new URLSearchParams({ search })}`,
    );
  savePlanningPolicy = (
    body: import("@nimbus-erp/shared").PlanningPolicySave,
  ) => this.request<void>("/planning/policies", { method: "PUT", body });
  mrpRuns = (page = 0, size = 20, startDate = "", endDate = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").MrpRun>>(
      `/planning/mrp-runs?${new URLSearchParams({ page: String(page), size: String(size), ...listDateParams(startDate, endDate) })}`,
    );
  mrpRun = (id: string) =>
    this.request<import("@nimbus-erp/shared").MrpRun>(
      `/planning/mrp-runs/${id}`,
    );
  calculateMrp = (
    body: { planningId: string; warehouseIds: string[] },
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").MrpRun>("/planning/mrp-runs", {
      method: "POST",
      body,
      idempotencyKey,
    });
  convertMrpBuy = (
    id: string,
    body: import("@nimbus-erp/shared").MrpBuyConvert,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").MrpRun>(
      `/planning/suggestions/${id}/purchase`,
      { method: "POST", body, idempotencyKey },
    );
  convertMrpMake = (
    id: string,
    body: import("@nimbus-erp/shared").MrpMakeConvert,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").MrpRun>(
      `/planning/suggestions/${id}/production`,
      { method: "POST", body, idempotencyKey },
    );
  dismissMrp = (id: string, body: { version: number; reason: string }) =>
    this.request<import("@nimbus-erp/shared").MrpRun>(
      `/planning/suggestions/${id}/dismiss`,
      { method: "POST", body },
    );
  capacityBoard = (startDate: string, endDate: string, workCenterId?: string) =>
    this.request<import("@nimbus-erp/shared").CapacityDay[]>(
      `/planning/capacity?${new URLSearchParams({ startDate, endDate, ...(workCenterId ? { workCenterId } : {}) })}`,
    );
  scheduleProduction = (
    id: string,
    body: { version: number; startDate: string; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").ScheduleResult>(
      `/planning/orders/${id}/schedule`,
      { method: "POST", body },
    );
  manuallyScheduleProduction = (
    id: string,
    operationId: string,
    body: {
      orderVersion: number;
      operationVersion: number;
      startDate: string;
      endDate: string;
      allowOverload: boolean;
      reason: string;
    },
  ) =>
    this.request<import("@nimbus-erp/shared").ScheduleResult>(
      `/planning/orders/${id}/operations/${operationId}/schedule`,
      { method: "PUT", body },
    );
  clearProductionSchedule = (
    id: string,
    body: { version: number; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").ScheduleResult>(
      `/planning/orders/${id}/clear-schedule`,
      { method: "POST", body },
    );
  private csrfToken: string | null = null;
  private csrfRequest: Promise<string> | null = null;
  private csrfGeneration = 0;
  private loginType: "ERP" | "PLATFORM" = "ERP";

  constructor(private readonly baseUrl = "/api/v1") {}

  productionOptions = () =>
    this.request<ProductionOptions>("/manufacturing/production-options");
  inventoryProductionOptions = () =>
    this.request<ProductionOptions>("/inventory/production/options");
  inventoryProductionStockOptions = (productId: string, warehouseId: string) =>
    this.request<ProductionStockOption[]>(`/inventory/production/stock-options?${new URLSearchParams({ productId, warehouseId })}`);
  inventoryProductionDocuments = (kind: "ISSUE" | "RECEIPT", page = 0, size = 20, search = "", startDate = "", endDate = "") => {
    const query = new URLSearchParams({ kind, page: String(page), size: String(size), search });
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return this.request<PageResult<import("@nimbus-erp/shared").InventoryProductionDocument>>(`/inventory/production/documents?${query}`);
  };
  inventoryProductionBatches = (page = 0, size = 20, search = "", status = "") =>
    this.request<PageResult<ProductionOrder>>(`/inventory/production/batches?${new URLSearchParams({ page: String(page), size: String(size), search, status })}`);
  inventoryProductionBatch = (id: string) =>
    this.request<ProductionOrder>(`/inventory/production/batches/${id}`);
  createInventoryProductionIssue = (body: import("@nimbus-erp/shared").DirectProductionIssue, idempotencyKey: string) =>
    this.request<ProductionOrder>("/inventory/production/issues", { method: "POST", body, idempotencyKey });
  createInventoryProductionReceipt = (body: import("@nimbus-erp/shared").DirectProductionReceipt, idempotencyKey: string) =>
    this.request<ProductionOrder>("/inventory/production/receipts", { method: "POST", body, idempotencyKey });
  receiveInventoryProduction = (id: string, body: Omit<ProductionReceipt, "allowShortage"> & { qualityConfirmed: boolean }, idempotencyKey: string) =>
    this.request<ProductionOrder>(`/inventory/production/batches/${id}/receipts`, { method: "POST", body, idempotencyKey });
  productionEngineering = (productId: string) =>
    this.request<ProductionEngineering>(
      `/manufacturing/engineering-options?${new URLSearchParams({ productId })}`,
    );
  productionStockOptions = (
    productId: string,
    warehouseId: string,
    search = "",
  ) =>
    this.request<ProductionStockOption[]>(
      `/manufacturing/stock-options?${new URLSearchParams({ productId, warehouseId, search })}`,
    );
  productionOrders = (page = 0, size = 20, search = "", status = "", startDate = "", endDate = "") => {
    const query = new URLSearchParams({ page: String(page), size: String(size), search, status });
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return this.request<PageResult<ProductionOrder>>(`/manufacturing/orders?${query}`);
  };
  productionOrder = (id: string) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}`);
  createProductionOrder = (body: ProductionCreate, idempotencyKey: string) =>
    this.request<ProductionOrder>("/manufacturing/orders", {
      method: "POST",
      body,
      idempotencyKey,
    });
  actOnProductionOrder = (id: string, body: ProductionAction) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/action`, {
      method: "POST",
      body,
    });
  transferWorkflow = (
    id: string,
    body: import("@nimbus-erp/shared").WorkflowTransfer,
  ) =>
    this.request<import("@nimbus-erp/shared").WorkflowInstance>(
      `/workflow/instances/${id}/transfer`,
      { method: "POST", body },
    );
  dispatchProductionOrder = (id: string, body: ProductionDispatch) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/dispatch`, {
      method: "PUT",
      body,
    });
  splitProductionOrder = (
    id: string,
    body: ProductionSplit,
    idempotencyKey: string,
  ) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/split`, {
      method: "POST",
      body,
      idempotencyKey,
    });
  issueProductionMaterial = (
    id: string,
    body: ProductionIssue,
    idempotencyKey: string,
  ) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/issues`, {
      method: "POST",
      body,
      idempotencyKey,
    });
  returnProductionMaterial = (
    id: string,
    body: ProductionReturn,
    idempotencyKey: string,
  ) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/returns`, {
      method: "POST",
      body,
      idempotencyKey,
    });
  allocateProductionCost = (
    id: string,
    body: ProductionCost,
    idempotencyKey: string,
  ) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/costs`, {
      method: "POST",
      body,
      idempotencyKey,
    });
  receiveProduction = (
    id: string,
    body: ProductionReceipt,
    idempotencyKey: string,
  ) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/receipts`, {
      method: "POST",
      body,
      idempotencyKey,
    });
  receiveSimpleProduction = (id: string, body: Omit<ProductionReceipt, "allowShortage"> & { qualityConfirmed: boolean }, idempotencyKey: string) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/simple-receipts`, { method: "POST", body, idempotencyKey });
  createDirectProductionIssue = (body: import("@nimbus-erp/shared").DirectProductionIssue, idempotencyKey: string) =>
    this.request<ProductionOrder>("/manufacturing/direct-issues", { method: "POST", body, idempotencyKey });
  createDirectProductionReceipt = (body: import("@nimbus-erp/shared").DirectProductionReceipt, idempotencyKey: string) =>
    this.request<ProductionOrder>("/manufacturing/direct-receipts", { method: "POST", body, idempotencyKey });
  actOnProductionOperation = (
    id: string,
    operationId: string,
    body: ProductionOperationAction,
  ) =>
    this.request<ProductionOrder>(
      `/manufacturing/orders/${id}/operations/${operationId}/action`,
      { method: "POST", body },
    );
  reportProduction = (
    id: string,
    operationId: string,
    body: ProductionReportCreate,
    idempotencyKey: string,
  ) =>
    this.request<ProductionOrder>(
      `/manufacturing/orders/${id}/operations/${operationId}/reports`,
      { method: "POST", body, idempotencyKey },
    );
  reverseProductionMovement = (
    id: string,
    movementId: string,
    body: ProductionReverse,
  ) =>
    this.request<ProductionOrder>(
      `/manufacturing/orders/${id}/movements/${movementId}/reverse`,
      { method: "POST", body },
    );
  reverseProductionReport = (
    id: string,
    reportId: string,
    body: ProductionReverse,
  ) =>
    this.request<ProductionOrder>(
      `/manufacturing/orders/${id}/reports/${reportId}/reverse`,
      { method: "POST", body },
    );
  scrapProduction = (
    id: string,
    body: ProductionAction,
    idempotencyKey: string,
  ) =>
    this.request<ProductionOrder>(`/manufacturing/orders/${id}/scrap`, {
      method: "POST",
      body,
      idempotencyKey,
    });

  manufacturingProducts = (search = "") =>
    this.request<ManufacturingOption[]>(
      `/manufacturing/product-options?${new URLSearchParams({ search })}`,
    );
  workCenters = () => this.request<WorkCenter[]>("/manufacturing/work-centers");
  saveWorkCenter = (
    id: string | undefined,
    body: WorkCenterSave,
    idempotencyKey: string,
  ) =>
    this.request<WorkCenter>(
      `/manufacturing/work-centers${id ? `/${id}` : ""}`,
      { method: id ? "PUT" : "POST", body, idempotencyKey },
    );
  workCalendar = (workCenterId: string, startDate: string, endDate: string) =>
    this.request<WorkCalendarDay[]>(
      `/manufacturing/calendar?${new URLSearchParams({ workCenterId, startDate, endDate })}`,
    );
  saveWorkCalendar = (body: WorkCalendarSave) =>
    this.request<WorkCalendarDay>("/manufacturing/calendar", {
      method: "PUT",
      body,
    });
  boms = (page = 0, size = 20, search = "") =>
    this.request<PageResult<Bom>>(
      `/manufacturing/boms?${new URLSearchParams({ page: String(page), size: String(size), search })}`,
    );
  bom = (id: string) => this.request<Bom>(`/manufacturing/boms/${id}`);
  saveBom = (id: string | undefined, body: BomSave, idempotencyKey: string) =>
    this.request<Bom>(`/manufacturing/boms${id ? `/${id}` : ""}`, {
      method: id ? "PUT" : "POST",
      body,
      idempotencyKey,
    });
  actOnBom = (
    id: string,
    action: "PUBLISH" | "RETIRE",
    version: number,
    reason: string,
  ) =>
    this.request<Bom>(`/manufacturing/boms/${id}/${action}`, {
      method: "POST",
      body: { version, reason },
    });
  routings = (page = 0, size = 20, search = "") =>
    this.request<PageResult<Routing>>(
      `/manufacturing/routings?${new URLSearchParams({ page: String(page), size: String(size), search })}`,
    );
  routing = (id: string) =>
    this.request<Routing>(`/manufacturing/routings/${id}`);
  saveRouting = (
    id: string | undefined,
    body: RoutingSave,
    idempotencyKey: string,
  ) =>
    this.request<Routing>(`/manufacturing/routings${id ? `/${id}` : ""}`, {
      method: id ? "PUT" : "POST",
      body,
      idempotencyKey,
    });
  actOnRouting = (
    id: string,
    action: "PUBLISH" | "RETIRE",
    version: number,
    reason: string,
  ) =>
    this.request<Routing>(`/manufacturing/routings/${id}/${action}`, {
      method: "POST",
      body: { version, reason },
    });

  accountSet = () => this.request<AccountSet>("/finance/account-set");

  updateAccountSet = (name: string, version: number) =>
    this.request<AccountSet>("/finance/account-set", {
      method: "PATCH",
      body: { name, version },
    });

  createAccountingYear = (year: number) =>
    this.request<AccountingPeriod[]>("/finance/years", {
      method: "POST",
      body: { year },
    });

  createAccountSubject = (input: SubjectWriteRequest, idempotencyKey: string) =>
    this.request<AccountSubject>("/finance/subjects", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  updateAccountSubject = (id: string, input: SubjectWriteRequest) =>
    this.request<AccountSubject>(`/finance/subjects/${id}`, {
      method: "PATCH",
      body: input,
    });

  changeAccountSubjectStatus = (
    id: string,
    status: "ACTIVE" | "DISABLED",
    version: number,
    reason: string,
  ) =>
    this.request<AccountSubject>(`/finance/subjects/${id}/status`, {
      method: "PATCH",
      body: { status, version, reason },
    });

  setSessionScope(loginType: "ERP" | "PLATFORM") {
    this.loginType = loginType;
    this.csrfToken = null;
    this.csrfRequest = null;
    this.csrfGeneration++;
  }

  platformHostHealth = () => this.request<import("@nimbus-erp/shared").PlatformHostHealth>("/platform/operations/host-health");

  private async request<T>(
    path: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { body, idempotencyKey, ...requestInit } = options;
    const headers = new Headers(options.headers);
    headers.set("X-Login-Type", this.loginType);
    if(this.loginType === "ERP" && this.expectedTenant && !path.startsWith("/auth/")) headers.set("X-Tenant-Id",this.expectedTenant);
    const method = (options.method ?? "GET").toUpperCase();
    if (
      this.demoWriteProtected &&
      !["GET", "HEAD", "OPTIONS"].includes(method) &&
      !["/auth/logout", "/auth/login", "/auth/platform-access"].includes(path)
    ) {
      const demoMessage =
        "演示账号可体验完整操作流程，但不能新增、编辑、审核或删除数据。";
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("erp:demo-write-blocked", {
            detail: { message: demoMessage, method, path },
          }),
        );
      }
      throw new ApiError(demoMessage, 403);
    }
    if (
      !["GET", "HEAD", "OPTIONS"].includes(method) &&
      !["/auth/login","/auth/platform-access"].includes(path)
    ) {
      headers.set("X-XSRF-TOKEN", await this.ensureCsrf());
    }
    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;
    const isRawBody = typeof Blob !== "undefined" && body instanceof Blob;
    if (body !== undefined && !isFormData && !isRawBody)
      headers.set("Content-Type", "application/json");
    if (idempotencyKey) headers.set("Idempotency-Key", idempotencyKey);

    const init: RequestInit = {
      ...requestInit,
      credentials: "include",
      headers,
    };
    if (body !== undefined)
      init.body =
        isFormData || isRawBody ? (body as BodyInit) : JSON.stringify(body);
    const response = await fetch(`${this.baseUrl}${path}`, init);

    const payload = (await response
      .json()
      .catch(() => null)) as ApiEnvelope<T> | null;
    if (!response.ok || !payload?.success) {
      const details = payload as
        (ApiEnvelope<T> & { fieldErrors?: Record<string, string> }) | null;
      const approvalKind = details?.fieldErrors?.approvalKind;
      const sourceId = details?.fieldErrors?.sourceId;
      if (
        this.loginType === "ERP" &&
        approvalKind &&
        sourceId &&
        body &&
        typeof body === "object" &&
        !path.startsWith("/workflow/")
      ) {
        this.approvalHandler?.({
          kind: approvalKind as import("@nimbus-erp/shared").WorkflowKind,
          sourceId,
          requestKey: idempotencyKey ?? null,
          payload: body as Record<string, unknown>,
        });
      }
      throw new ApiError(
        details?.message ?? `请求失败（HTTP ${response.status}）`,
        response.status,
        details?.traceId,
        details?.fieldErrors,
      );
    }
    return payload.data;
  }

  private async ensureCsrf(): Promise<string> {
    if (this.csrfToken) return this.csrfToken;
    if (this.csrfRequest) return this.csrfRequest;
    const generation = this.csrfGeneration;
    const scope = this.loginType;
    const pending = this.fetchCsrf(scope, generation);
    this.csrfRequest = pending;
    try {
      return await pending;
    } finally {
      if (this.csrfRequest === pending) this.csrfRequest = null;
    }
  }

  private async fetchCsrf(
    scope: "ERP" | "PLATFORM",
    generation: number,
  ): Promise<string> {
    const response = await fetch(`${this.baseUrl}/auth/csrf`, {
      credentials: "include",
      headers: { "X-Login-Type": scope },
    });
    const payload = (await response.json().catch(() => null)) as ApiEnvelope<{
      token: string;
    }> | null;
    if (!response.ok || !payload?.success) {
      throw new ApiError(
        payload?.message ?? "无法初始化安全令牌",
        response.status,
        payload?.traceId,
      );
    }
    if (generation !== this.csrfGeneration)
      throw new ApiError("登录上下文已变化，请重新操作", 409);
    this.csrfToken = payload.data.token;
    return this.csrfToken;
  }

  login = (input: LoginRequest) => {
    this.setSessionScope(input.loginType);
    return this.request<SessionUser>("/auth/login", {
      method: "POST",
      body: input,
    });
  };

  logout = async () => {
    try {
      return await this.request<void>("/auth/logout", { method: "POST" });
    } finally {
      this.setSessionScope(this.loginType);
    }
  };

  exchangePlatformAccess = (token: string) => {
    this.setSessionScope("ERP");
    return this.request<SessionUser>("/auth/platform-access", { method: "POST", body: { token } });
  };
  issueTenantAccess = (id: string, reason: string) => this.request<import("@nimbus-erp/shared").TenantAccessIssued>(`/platform/tenants/${id}/access-grants`, {method:"POST",body:{reason}});
  tenantAccessGrants = (id: string) => this.request<import("@nimbus-erp/shared").TenantAccessGrant[]>(`/platform/tenants/${id}/access-grants`);
  revokeTenantAccess = (tenantId: string, id: string) => this.request<void>(`/platform/tenants/${tenantId}/access-grants/${id}`,{method:"DELETE"});
  tenantDirectoryOverview = (search: string, status: string, filters: import("@nimbus-erp/shared").TenantFilters) => {
    const query = new URLSearchParams({search,status});
    for(const [key,value] of Object.entries(filters)) if(value !== undefined && value !== "") query.set(key,String(value));
    return this.request<import("@nimbus-erp/shared").TenantDirectoryOverview>(`/platform/tenants/overview?${query}`);
  };
  suggestCode = async (kind: import("@nimbus-erp/shared").CodeSuggestionKind, name: string) => {
    const { nameCodePrefix } = await import("@nimbus-erp/shared/nameCode");
    const query = new URLSearchParams({ kind, prefix: nameCodePrefix(name) });
    const path = this.loginType === "PLATFORM" ? "/platform/code-suggestions" : "/code-suggestions";
    return (await this.request<{ code: string }>(`${path}?${query}`)).code;
  };
  me = () => this.request<SessionUser>("/auth/me");

  changePlatformPassword = (currentPassword: string, newPassword: string) =>
    this.request<void>("/auth/platform/change-password", {
      method: "POST",
      body: { currentPassword, newPassword },
    });

  changePlatformProfile = (username: string, displayName: string, currentPassword: string) =>
    this.request<void>("/auth/platform/change-profile", {
      method: "POST", body: { username, displayName, currentPassword },
    });

  platformAccounts = () => this.request<PlatformAccount[]>("/platform/accounts");

  createPlatformAccount = (body: PlatformAccountCreate) =>
    this.request<PlatformAccount>("/platform/accounts", { method: "POST", body });

  updatePlatformAccount = (id: string, body: PlatformAccountUpdate) =>
    this.request<PlatformAccount>(`/platform/accounts/${id}`, { method: "PUT", body });

  resetPlatformAccountPassword = (id: string, password: string, version: number, reason: string) =>
    this.request<void>(`/platform/accounts/${id}/reset-password`, {
      method: "POST", body: { password, version, reason },
    });

  workspace = () => this.request<WorkspaceSummary>("/erp/workspace");

  masterDataOptions = () =>
    this.request<MasterDataOptions>("/master-data/options");

  units = () => this.request<UnitItem[]>("/master-data/units");

  createUnit = (input: UnitCreateRequest) =>
    this.request<UnitItem>("/master-data/units", {
      method: "POST",
      body: input,
    });

  categories = () => this.request<CategoryItem[]>("/master-data/categories");

  createCategory = (input: CategoryCreateRequest) =>
    this.request<CategoryItem>("/master-data/categories", {
      method: "POST",
      body: input,
    });

  products = (page = 0, size = 20, search = "", status = "") =>
    this.masterPage<ProductItem>("products", page, size, search, status);

  customers = (page = 0, size = 20, search = "", status = "") =>
    this.masterPage<CustomerItem>("customers", page, size, search, status);

  suppliers = (page = 0, size = 20, search = "", status = "") =>
    this.masterPage<SupplierItem>("suppliers", page, size, search, status);

  warehouses = (page = 0, size = 20, search = "", status = "") =>
    this.masterPage<WarehouseItem>("warehouses", page, size, search, status);

  createProduct = (input: ProductCreateRequest) =>
    this.request<ProductItem>("/master-data/products", {
      method: "POST",
      body: input,
    });

  createCustomer = (input: CustomerCreateRequest) =>
    this.request<CustomerItem>("/master-data/customers", {
      method: "POST",
      body: input,
    });

  createSupplier = (input: SupplierCreateRequest) =>
    this.request<SupplierItem>("/master-data/suppliers", {
      method: "POST",
      body: input,
    });

  createWarehouse = (input: WarehouseCreateRequest) =>
    this.request<WarehouseItem>("/master-data/warehouses", {
      method: "POST",
      body: input,
    });

  changeMasterDataStatus = (
    target: string,
    id: string,
    status: "ACTIVE" | "DISABLED",
    version: number,
  ) =>
    this.request<void>(`/master-data/${target}/${id}/status`, {
      method: "PATCH",
      body: { status, version },
    });

  tradeExecution = (domain: "sales" | "purchase", id: string) =>
    this.request<TradeExecutionDocument>(
      `/${domain}/${domain === "sales" ? "outbounds" : "inbounds"}/${id}`,
    );
  tradeReturn = (domain: "sales" | "purchase", id: string) =>
    this.request<TradeReturnDocument>(`/${domain}/returns/${id}`);
  updateTradeOrder = (
    domain: "sales" | "purchase",
    id: string,
    document: TradeOrderCreateRequest,
    version: number,
  ) =>
    this.request<TradeOrder>(`/${domain}/orders/${id}`, {
      method: "PUT",
      body: { document, version },
    });
  copyTradeOrder = (
    domain: "sales" | "purchase",
    id: string,
    document: TradeOrderCreateRequest,
    version: number,
    idempotencyKey: string,
  ) =>
    this.request<TradeOrder>(`/${domain}/orders/${id}/copy`, {
      method: "POST",
      body: { document, version },
      idempotencyKey,
    });
  actionTradeOrder = (
    domain: "sales" | "purchase",
    id: string,
    action: "WITHDRAW" | "CLOSE" | "VOID",
    version: number,
    reason: string,
  ) =>
    this.request<TradeOrder>(`/${domain}/orders/${id}/action`, {
      method: "PATCH",
      body: { action, version, reason },
    });
  salesSummary = () => this.request<TradeSummary>("/sales/summary");

  salesOrders = (
    page = 0,
    size = 20,
    search = "",
    auditStatus = "",
    businessStatus = "",
    startDate = "",
    endDate = "",
  ) =>
    this.tradeOrders("sales", page, size, search, auditStatus, businessStatus, startDate, endDate);

  salesOrder = (id: string) => this.request<TradeOrder>(`/sales/orders/${id}`);
  salesReservations = (id: string) =>
    this.request<import("@nimbus-erp/shared").SalesReservation[]>(
      `/sales/orders/${id}/reservations`,
    );
  salesReservationOptions = (id: string) =>
    this.request<import("@nimbus-erp/shared").SalesStockOption[]>(
      `/sales/orders/${id}/reservations/options`,
    );
  reserveSalesStock = (
    id: string,
    body: import("@nimbus-erp/shared").SalesReserveRequest,
  ) =>
    this.request<import("@nimbus-erp/shared").SalesReservation[]>(
      `/sales/orders/${id}/reservations`,
      { method: "POST", body },
    );
  releaseSalesStock = (
    orderId: string,
    id: string,
    body: { orderVersion: number; version: number; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").SalesReservation[]>(
      `/sales/orders/${orderId}/reservations/${id}/release`,
      { method: "POST", body },
    );

  createSalesOrder = (
    input: TradeOrderCreateRequest & { customerId: string },
    idempotencyKey: string,
  ) =>
    this.request<TradeOrder>("/sales/orders", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  auditSalesOrder = (
    id: string,
    action: "APPROVE" | "REJECT",
    version: number,
    reason?: string,
  ) =>
    this.request<TradeOrder>(`/sales/orders/${id}/audit`, {
      method: "PATCH",
      body: { action, version, reason },
    });

  salesOutbounds = (limit = 30) =>
    this.request<TradeExecutionDocument[]>(`/sales/outbounds?limit=${limit}`);

  salesOutboundPage = (page = 0, size = 20, search = "", startDate = "", endDate = "", returnableOnly = false) =>
    this.request<PageResult<TradeExecutionDocument>>(
      `/sales/outbounds/page?${new URLSearchParams({ page: String(page), size: String(size), search, startDate, endDate, returnableOnly: String(returnableOnly) })}`,
    );

  createSalesOutbound = (
    input: TradeExecutionRequest,
    idempotencyKey: string,
  ) =>
    this.request<TradeExecutionDocument>("/sales/outbounds", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  createDirectSalesOutbound = (input: DirectTradeExecutionRequest, idempotencyKey: string) =>
    this.request<TradeExecutionDocument>("/sales/outbounds/direct", {
      method: "POST", body: input, idempotencyKey,
    });

  salesReturns = (limit = 30) =>
    this.request<TradeReturnDocument[]>(`/sales/returns?limit=${limit}`);

  salesReturnPage = (page = 0, size = 20, search = "", startDate = "", endDate = "") =>
    this.request<PageResult<TradeReturnDocument>>(
      `/sales/returns/page?${new URLSearchParams({ page: String(page), size: String(size), search, startDate, endDate })}`,
    );

  createSalesReturn = (
    input: TradeReturnRequest & { outboundId: string },
    idempotencyKey: string,
  ) =>
    this.request<TradeReturnDocument>("/sales/returns", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  purchaseSummary = () => this.request<TradeSummary>("/purchase/summary");

  purchaseOrders = (
    page = 0,
    size = 20,
    search = "",
    auditStatus = "",
    businessStatus = "",
    startDate = "",
    endDate = "",
  ) =>
    this.tradeOrders(
      "purchase",
      page,
      size,
      search,
      auditStatus,
      businessStatus,
      startDate,
      endDate,
    );

  purchaseOrder = (id: string) =>
    this.request<TradeOrder>(`/purchase/orders/${id}`);

  createPurchaseOrder = (
    input: TradeOrderCreateRequest & { supplierId: string },
    idempotencyKey: string,
  ) =>
    this.request<TradeOrder>("/purchase/orders", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  auditPurchaseOrder = (
    id: string,
    action: "APPROVE" | "REJECT",
    version: number,
    reason?: string,
  ) =>
    this.request<TradeOrder>(`/purchase/orders/${id}/audit`, {
      method: "PATCH",
      body: { action, version, reason },
    });

  purchaseInbounds = (limit = 30) =>
    this.request<TradeExecutionDocument[]>(`/purchase/inbounds?limit=${limit}`);

  purchaseInboundPage = (page = 0, size = 20, search = "", startDate = "", endDate = "", returnableOnly = false) =>
    this.request<PageResult<TradeExecutionDocument>>(
      `/purchase/inbounds/page?${new URLSearchParams({ page: String(page), size: String(size), search, startDate, endDate, returnableOnly: String(returnableOnly) })}`,
    );

  createPurchaseInbound = (
    input: TradeExecutionRequest,
    idempotencyKey: string,
  ) =>
    this.request<TradeExecutionDocument>("/purchase/inbounds", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  createDirectPurchaseInbound = (input: DirectTradeExecutionRequest, idempotencyKey: string) =>
    this.request<TradeExecutionDocument>("/purchase/inbounds/direct", {
      method: "POST", body: input, idempotencyKey,
    });

  purchaseReturns = (limit = 30) =>
    this.request<TradeReturnDocument[]>(`/purchase/returns?limit=${limit}`);

  purchaseReturnPage = (page = 0, size = 20, search = "", startDate = "", endDate = "") =>
    this.request<PageResult<TradeReturnDocument>>(
      `/purchase/returns/page?${new URLSearchParams({ page: String(page), size: String(size), search, startDate, endDate })}`,
    );

  createPurchaseReturn = (
    input: TradeReturnRequest & { inboundId: string },
    idempotencyKey: string,
  ) =>
    this.request<TradeReturnDocument>("/purchase/returns", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  inventorySummary = () =>
    this.request<import("@nimbus-erp/shared").InventorySummary>(
      "/inventory/summary",
    );

  stockBalances = (
    page = 0,
    size = 20,
    search = "",
    warehouseId = "",
    lowStockOnly = false,
    productId = "",
  ) => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
      search,
      lowStockOnly: String(lowStockOnly),
    });
    if (warehouseId) query.set("warehouseId", warehouseId);
    if (productId) query.set("productId", productId);
    return this.request<PageResult<StockBalance>>(
      `/inventory/balances?${query}`,
    );
  };

  stockLogs = (page = 0, size = 20, search = "", warehouseId = "", startDate = "", endDate = "") => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
      search,
    });
    if (warehouseId) query.set("warehouseId", warehouseId);
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return this.request<PageResult<StockLog>>(`/inventory/logs?${query}`);
  };

  miscStockMovements = (
    kind: "OTHER_INBOUND" | "OTHER_OUTBOUND",
    page = 0,
    size = 20,
    search = "",
    startDate = "",
    endDate = "",
  ) => {
    const query = new URLSearchParams({ kind, page: String(page), size: String(size), search });
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return this.request<PageResult<MiscStockMovement>>(`/inventory/other-movements?${query}`);
  };

  createMiscStockMovement = (input: MiscStockMovementRequest, key: string) =>
    this.request<MiscStockMovement>("/inventory/other-movements", {
      method: "POST",
      body: input,
      idempotencyKey: key,
    });

  stockTransfers = (limit = 30) =>
    this.request<StockTransfer[]>(`/inventory/transfers?limit=${limit}`);

  stockTransferPage = (page = 0, size = 20, search = "", startDate = "", endDate = "") => {
    const query = new URLSearchParams({ page: String(page), size: String(size), search });
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return this.request<PageResult<StockTransfer>>(`/inventory/transfers/page?${query}`);
  };

  createStockTransfer = (input: StockTransferRequest, idempotencyKey: string) =>
    this.request<StockTransfer>("/inventory/transfers", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  receiveStockTransfer = (
    id: string,
    input: import("@nimbus-erp/shared").StockTransferActionRequest,
  ) =>
    this.request<StockTransfer>(`/inventory/transfers/${id}/receive`, {
      method: "POST",
      body: input,
    });

  cancelStockTransfer = (
    id: string,
    input: import("@nimbus-erp/shared").StockTransferActionRequest,
  ) =>
    this.request<StockTransfer>(`/inventory/transfers/${id}/cancel`, {
      method: "POST",
      body: input,
    });

  stockChecks = (limit = 30) =>
    this.request<StockCheck[]>(`/inventory/checks?limit=${limit}`);

  inventoryCountSessionPage = (page = 0, size = 20, search = "", startDate = "", endDate = "") => {
    const query = new URLSearchParams({ page: String(page), size: String(size), search });
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return this.request<PageResult<import("@nimbus-erp/shared").InventoryCountSession>>(`/inventory/count-sessions/page?${query}`);
  };

  createStockCheck = (input: StockCheckRequest, idempotencyKey: string) =>
    this.request<StockCheck>("/inventory/checks", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  financeSummary = () => this.request<FinanceSummary>("/finance/summary");
  voucherAttachments = (voucherId: string) =>
    this.request<import("@nimbus-erp/shared").VoucherAttachment[]>(
      `/finance/vouchers/${voucherId}/attachments`,
    );
  uploadVoucherAttachment = (voucherId: string, file: File) => {
    const body = new FormData();
    body.append("file", file);
    return this.request<import("@nimbus-erp/shared").VoucherAttachment>(
      `/finance/vouchers/${voucherId}/attachments`,
      { method: "POST", body },
    );
  };
  downloadVoucherAttachment = async (
    voucherId: string,
    attachmentId: string,
  ) => {
    const response = await fetch(
      `${this.baseUrl}/finance/vouchers/${voucherId}/attachments/${attachmentId}/content`,
      { credentials: "include", headers: { "X-Login-Type": this.loginType } },
    );
    if (!response.ok) {
      const payload = (await response
        .json()
        .catch(() => null)) as ApiEnvelope<unknown> | null;
      throw new ApiError(
        payload?.message ?? `下载失败（HTTP ${response.status}）`,
        response.status,
        payload?.traceId,
      );
    }
    return response.blob();
  };
  deleteVoucherAttachment = (
    voucherId: string,
    attachmentId: string,
    version: number,
  ) =>
    this.request<void>(
      `/finance/vouchers/${voucherId}/attachments/${attachmentId}?version=${version}`,
      { method: "DELETE" },
    );
  reconciliationStatements = (direction = "", search = "", status = "", startDate = "", endDate = "", page = 0, size = 20) =>
    this.request<PageResult<import("@nimbus-erp/shared").ReconciliationStatement>>(
      `/finance/reconciliations/page?${new URLSearchParams({ direction, search, status,page:String(page),size:String(size), ...listDateParams(startDate,endDate) })}`,
    );
  reconciliationStatement = (id: string) =>
    this.request<import("@nimbus-erp/shared").ReconciliationStatement>(
      `/finance/reconciliations/${id}`,
    );
  createReconciliationStatement = (
    body: import("@nimbus-erp/shared").ReconciliationCreate,
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").ReconciliationStatement>(
      "/finance/reconciliations",
      { method: "POST", body, idempotencyKey: key },
    );
  actReconciliationStatement = (
    id: string,
    body: import("@nimbus-erp/shared").ReconciliationAction,
  ) =>
    this.request<import("@nimbus-erp/shared").ReconciliationStatement>(
      `/finance/reconciliations/${id}/action`,
      { method: "PATCH", body },
    );
  saveSettlementAccount = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").SettlementAccountWrite,
    key: string,
  ) =>
    this.request<SettlementAccount>(
      id
        ? `/finance/settlement-accounts/${id}`
        : "/finance/settlement-accounts",
      { method: id ? "PATCH" : "POST", body, idempotencyKey: key },
    );
  businessSubjectMappings = () =>
    this.request<import("@nimbus-erp/shared").BusinessSubjectMapping[]>(
      "/finance/subject-mappings",
    );
  updateBusinessSubjectMapping = (
    code: string,
    body: { subjectId: string; version: number; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").BusinessSubjectMapping[]>(
      `/finance/subject-mappings/${code}`,
      { method: "PATCH", body },
    );
  purchaseExpenses = (page = 0, size = 20, search = "", status = "", startDate = "", endDate = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").PurchaseExpense>>(
      `/purchase/expenses?${new URLSearchParams({page:String(page),size:String(size),search,status,...listDateParams(startDate,endDate)})}`,
    );
  purchaseExpense = (id: string) =>
    this.request<import("@nimbus-erp/shared").PurchaseExpenseDetail>(
      `/purchase/expenses/${id}`,
    );
  purchaseExpenseTargets = (search = "") =>
    this.request<import("@nimbus-erp/shared").PurchaseExpenseTarget[]>(
      `/purchase/expenses/targets?search=${encodeURIComponent(search)}`,
    );
  savePurchaseExpense = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").PurchaseExpenseSave,
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").PurchaseExpenseDetail>(
      `/purchase/expenses${id ? `/${id}` : ""}`,
      { method: id ? "PUT" : "POST", body, idempotencyKey: key },
    );
  actPurchaseExpense = (
    id: string,
    body: import("@nimbus-erp/shared").PurchaseExpenseAction,
  ) =>
    this.request<import("@nimbus-erp/shared").PurchaseExpenseDetail>(
      `/purchase/expenses/${id}/action`,
      { method: "POST", body },
    );
  inventoryCostAdjustments = (page = 0, size = 20, startDate = "", endDate = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").InventoryCostAdjustment>>(
      `/finance/cost-adjustments/page?${new URLSearchParams({page:String(page),size:String(size),...listDateParams(startDate,endDate)})}`,
    );
  createInventoryCostAdjustment = (
    body: import("@nimbus-erp/shared").InventoryCostRequest,
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").InventoryCostAdjustment>(
      "/finance/cost-adjustments",
      { method: "POST", body, idempotencyKey: key },
    );
  reverseInventoryCostAdjustment = (
    id: string,
    body: import("@nimbus-erp/shared").FinanceReversalRequest,
  ) =>
    this.request<import("@nimbus-erp/shared").InventoryCostAdjustment>(
      `/finance/cost-adjustments/${id}/reverse`,
      { method: "POST", body },
    );
  inventoryCostRecalculations = (page = 0, size = 20, startDate = "", endDate = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").InventoryCostRecalculation>>(
      `/finance/cost-recalculations/page?${new URLSearchParams({page:String(page),size:String(size),...listDateParams(startDate,endDate)})}`,
    );
  inventoryCostRecalculation = (id: string) =>
    this.request<import("@nimbus-erp/shared").InventoryCostRecalculation>(
      `/finance/cost-recalculations/${id}`,
    );
  createInventoryCostRecalculation = (
    body: import("@nimbus-erp/shared").InventoryCostRecalculationRequest,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").InventoryCostRecalculation>(
      "/finance/cost-recalculations",
      { method: "POST", body, idempotencyKey },
    );
  settlementOffsets = (page = 0, size = 20, startDate = "", endDate = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").SettlementOffset>>(
      `/finance/offsets/page?${new URLSearchParams({page:String(page),size:String(size),...listDateParams(startDate,endDate)})}`,
    );
  createSettlementOffset = (
    body: {
      direction: OpenItemDirection;
      positiveId: string;
      negativeId: string;
      amount: number;
      businessDate: string;
      reason: string;
    },
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").SettlementOffset>(
      "/finance/offsets",
      { method: "POST", body, idempotencyKey: key },
    );
  reverseSettlementOffset = (
    id: string,
    body: import("@nimbus-erp/shared").FinanceReversalRequest,
  ) =>
    this.request<import("@nimbus-erp/shared").SettlementOffset>(
      `/finance/offsets/${id}/reverse`,
      { method: "POST", body },
    );
  withdrawCashAudit = (
    direction: CashDirection,
    id: string,
    version: number,
    reason: string,
  ) =>
    this.request<CashOrder>(
      `/finance/cash-orders/${id}/withdraw-audit?direction=${direction}`,
      { method: "POST", body: { version, reason } },
    );
  financeTrace = (type: string, id: string) =>
    this.request<import("@nimbus-erp/shared").FinanceTrace>(
      `/finance/trace?${new URLSearchParams({ type, id })}`,
    );
  openingOptions = () =>
    this.request<import("@nimbus-erp/shared").OpeningOptions>(
      "/finance/opening-options",
    );
  auxiliaryLedger = (startDate: string, endDate: string) =>
    this.request<import("@nimbus-erp/shared").AuxiliaryLedgerRow[]>(
      `/finance/auxiliary-ledger?${new URLSearchParams({ startDate, endDate })}`,
    );
  batchVoucherAction = (
    action: "AUDIT" | "POST",
    items: Array<{ id: string; version: number }>,
  ) =>
    this.request<Voucher[]>("/finance/vouchers/batch-action", {
      method: "POST",
      body: { action, items },
    });
  reportSnapshots = () =>
    this.request<import("@nimbus-erp/shared").ReportSnapshot[]>(
      "/finance/report-snapshots",
    );
  createReportSnapshot = (
    body: { type: string; startDate: string; endDate: string },
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").ReportSnapshot>(
      "/finance/report-snapshots",
      { method: "POST", body, idempotencyKey: key },
    );
  advanceSources = (direction: CashDirection) =>
    this.request<import("@nimbus-erp/shared").AdvanceSource[]>(
      `/finance/advance-sources?direction=${direction}`,
    );
  unconfirmOpeningBalances = (year: number, version: number, reason: string) =>
    this.request<import("@nimbus-erp/shared").OpeningBalance>(
      `/finance/opening-balances/${year}/unconfirm`,
      { method: "POST", body: { version, reason } },
    );
  updateVoucherDraft = (
    id: string,
    version: number,
    voucher: VoucherCreateRequest,
  ) =>
    this.request<Voucher>(`/finance/vouchers/${id}`, {
      method: "PATCH",
      body: { version, voucher },
    });

  financeOpenItems = (
    direction: OpenItemDirection,
    page = 0,
    size = 20,
    search = "",
    status = "",
    itemId = "",
    partnerId = "", startDate = "", endDate = "", balanceSign = "",
  ) => {
    const query = new URLSearchParams({
      direction,
      page: String(page),
      size: String(size),
      search,
      status, ...listDateParams(startDate,endDate),
    });
    if (itemId) query.set("itemId", itemId);
    if (partnerId) query.set("partnerId", partnerId);
    if (balanceSign) query.set("balanceSign", balanceSign);
    return this.request<PageResult<OpenItem>>(`/finance/open-items?${query}`);
  };

  financeAging = () => this.request<AgingBucket[]>("/finance/aging");

  settlementAccounts = () =>
    this.request<SettlementAccount[]>("/finance/settlement-accounts");

  cashOrders = (direction: CashDirection, limit = 30) => {
    const query = new URLSearchParams({ direction, limit: String(limit) });
    return this.request<CashOrder[]>(`/finance/cash-orders?${query}`);
  };

  cashOrderPage = (direction: CashDirection, page = 0, size = 20, search = "", startDate = "", endDate = "") => {
    const query = new URLSearchParams({ direction, page: String(page), size: String(size), search });
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return this.request<PageResult<CashOrder>>(`/finance/cash-orders/page?${query}`);
  };

  createCashOrder = (
    direction: CashDirection,
    input: CashOrderCreateRequest,
    idempotencyKey: string,
  ) =>
    this.request<CashOrder>(
      `/finance/cash-orders?${new URLSearchParams({ direction })}`,
      { method: "POST", body: input, idempotencyKey },
    );

  auditCashOrder = (
    direction: CashDirection,
    id: string,
    action: "APPROVE" | "CANCEL",
    version: number,
    reason?: string,
  ) =>
    this.request<CashOrder>(
      `/finance/cash-orders/${id}/audit?${new URLSearchParams({ direction })}`,
      { method: "PATCH", body: { action, version, reason } },
    );

  accountSubjects = () => this.request<AccountSubject[]>("/finance/subjects");

  accountingPeriods = (year = new Date().getFullYear()) =>
    this.request<AccountingPeriod[]>(`/finance/periods?year=${year}`);

  financeAuxiliaryOptions = () =>
    this.request<FinanceAuxiliaryOptions>("/finance/auxiliary-options");

  vouchers = (
    page = 0,
    size = 20,
    search = "",
    status = "",
    startDate = "",
    endDate = "",
  ) => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
      search,
      status,
    });
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return this.request<PageResult<Voucher>>(`/finance/vouchers?${query}`);
  };

  voucher = (id: string) => this.request<Voucher>(`/finance/vouchers/${id}`);

  createVoucher = (input: VoucherCreateRequest, idempotencyKey: string) =>
    this.request<Voucher>("/finance/vouchers", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  actOnVoucher = (
    id: string,
    action: "AUDIT" | "POST" | "UNPOST" | "UNAUDIT" | "CANCEL",
    version: number,
    reason?: string,
  ) =>
    this.request<Voucher>(`/finance/vouchers/${id}/action`, {
      method: "PATCH",
      body: { action, version, reason },
    });

  openingBalances = (year: number) =>
    this.request<import("@nimbus-erp/shared").OpeningBalance>(
      `/finance/opening-balances?year=${year}`,
    );
  financeStatement = (type: string, startDate: string, endDate: string) =>
    this.request<import("@nimbus-erp/shared").FinanceStatement>(
      `/finance/statements?${new URLSearchParams({ type, startDate, endDate })}`,
    );
  classifyCashFlow = (id: string, version: number, category: string) =>
    this.request<Voucher>(`/finance/vouchers/${id}/cash-flow`, {
      method: "PATCH",
      body: { version, category },
    });
  settlementApplications = (page = 0, size = 20, startDate = "", endDate = "") =>
    this.request<PageResult<import("@nimbus-erp/shared").SettlementApplication>>(
      `/finance/applications/page?${new URLSearchParams({page:String(page),size:String(size),...listDateParams(startDate,endDate)})}`,
    );
  createSettlementApplication = (
    body: {
      direction: CashDirection;
      cashOrderId: string;
      cashVersion: number;
      businessDate: string;
      reason: string;
      allocations: Array<{ sourceId: string; amount: number }>;
    },
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").SettlementApplication>(
      "/finance/applications",
      { method: "POST", body, idempotencyKey },
    );
  reverseSettlementApplication = (
    id: string,
    body: import("@nimbus-erp/shared").FinanceReversalRequest,
  ) =>
    this.request<import("@nimbus-erp/shared").SettlementApplication>(
      `/finance/applications/${id}/reverse`,
      { method: "POST", body },
    );
  saveOpeningBalances = (body: {
    year: number;
    version: number;
    lines: import("@nimbus-erp/shared").OpeningLineInput[];
    stockLines?: import("@nimbus-erp/shared").OpeningStockLine[];
  }) =>
    this.request<import("@nimbus-erp/shared").OpeningBalance>(
      "/finance/opening-balances",
      { method: "POST", body },
    );
  confirmOpeningBalances = (year: number, version: number) =>
    this.request<import("@nimbus-erp/shared").OpeningBalance>(
      `/finance/opening-balances/${year}/confirm`,
      { method: "POST", body: { version } },
    );
  closingCheck = (id: string) =>
    this.request<import("@nimbus-erp/shared").ClosingCheck>(
      `/finance/periods/${id}/check`,
    );
  actOnPeriod = (
    id: string,
    body: { action: "CLOSE" | "REOPEN"; version: number; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").ClosingCheck>(
      `/finance/periods/${id}/action`,
      { method: "POST", body },
    );
  reverseVoucher = (
    id: string,
    body: import("@nimbus-erp/shared").FinanceReversalRequest,
  ) =>
    this.request<Voucher>(`/finance/vouchers/${id}/reverse`, {
      method: "POST",
      body,
    });
  reverseCashOrder = (
    direction: CashDirection,
    id: string,
    body: import("@nimbus-erp/shared").FinanceReversalRequest,
  ) =>
    this.request<CashOrder>(
      `/finance/cash-orders/${id}/reverse?direction=${direction}`,
      { method: "POST", body },
    );

  trialBalance = (startDate: string, endDate: string) => {
    const query = new URLSearchParams({ startDate, endDate });
    return this.request<TrialBalanceRow[]>(
      `/finance/ledger/trial-balance?${query}`,
    );
  };

  generalLedger = (subjectId: string, startDate: string, endDate: string) => {
    const query = new URLSearchParams({ subjectId, startDate, endDate });
    return this.request<GeneralLedgerRow[]>(`/finance/ledger/general?${query}`);
  };

  departments = () =>
    this.request<DepartmentItem[]>("/organization/departments");

  createDepartment = (input: DepartmentCreateRequest) =>
    this.request<DepartmentItem>("/organization/departments", {
      method: "POST",
      body: input,
    });

  employees = (page = 0, size = 20, search = "") => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
      search,
    });
    return this.request<PageResult<EmployeeItem>>(
      `/organization/employees?${query}`,
    );
  };

  createEmployee = (input: EmployeeCreateRequest) =>
    this.request<EmployeeItem>("/organization/employees", {
      method: "POST",
      body: input,
    });

  roles = () => this.request<RoleOption[]>("/organization/roles");

  permissionCatalog = () =>
    this.request<import("@nimbus-erp/shared").PermissionOption[]>(
      "/organization/permission-catalog",
    );
  dataScopeOptions = () =>
    this.request<import("@nimbus-erp/shared").DataScopeOptions>(
      "/organization/scope-options",
    );
  saveMasterRecord = (
    kind: string,
    document: unknown,
    id?: string,
    version = 0,
  ) =>
    this.request<unknown>(
      `/master-data/${encodeURIComponent(kind)}${id ? `/${encodeURIComponent(id)}` : ""}`,
      {
        method: id ? "PUT" : "POST",
        body: id ? { document, version } : document,
      },
    );
  enterpriseCatalogs = (
    kind: import("@nimbus-erp/shared").CatalogKind,
    page = 0,
    size = 20,
    search = "",
    status = "",
  ) =>
    this.request<PageResult<import("@nimbus-erp/shared").EnterpriseCatalog>>(
      `/master-data/extensions/catalogs?${new URLSearchParams({ kind, page: String(page), size: String(size), search, status })}`,
    );
  saveEnterpriseCatalog = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").EnterpriseCatalogSave,
  ) =>
    this.request<import("@nimbus-erp/shared").EnterpriseCatalog>(
      `/master-data/extensions/catalogs${id ? `/${id}` : ""}`,
      { method: id ? "PUT" : "POST", body },
    );
  enterpriseReferenceOptions = () =>
    this.request<import("@nimbus-erp/shared").EnterpriseReferenceOptions>(
      "/master-data/extensions/options",
    );
  unitConversions = (productId: string) =>
    this.request<import("@nimbus-erp/shared").UnitConversion[]>(
      `/master-data/extensions/unit-conversions?productId=${encodeURIComponent(productId)}`,
    );
  saveUnitConversion = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").UnitConversionSave,
  ) =>
    this.request<import("@nimbus-erp/shared").UnitConversion>(
      `/master-data/extensions/unit-conversions${id ? `/${id}` : ""}`,
      { method: id ? "PUT" : "POST", body },
    );
  convertQuantity = (productId: string, unitId: string, quantity: number) =>
    this.request<import("@nimbus-erp/shared").ConvertedQuantity>(
      `/master-data/extensions/convert-quantity?${new URLSearchParams({ productId, unitId, quantity: String(quantity) })}`,
    );
  partnerContacts = (partnerId: string, customer: boolean) =>
    this.request<import("@nimbus-erp/shared").PartnerContact[]>(
      `/master-data/extensions/contacts?${new URLSearchParams({ partnerId, customer: String(customer) })}`,
    );
  savePartnerContact = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").PartnerContactSave,
  ) =>
    this.request<import("@nimbus-erp/shared").PartnerContact>(
      `/master-data/extensions/contacts${id ? `/${id}` : ""}`,
      { method: id ? "PUT" : "POST", body },
    );
  managedRoles = () =>
    this.request<import("@nimbus-erp/shared").ManagedRole[]>(
      "/organization/role-management",
    );
  saveRole = (
    id: string | undefined,
    data: import("@nimbus-erp/shared").RoleSaveRequest,
  ) =>
    this.request<import("@nimbus-erp/shared").ManagedRole>(
      `/organization/role-management${id ? `/${id}` : ""}`,
      { method: id ? "PUT" : "POST", body: data },
    );
  employeeDetail = (id: string) =>
    this.request<import("@nimbus-erp/shared").EmployeeDetail>(
      `/organization/employees/${id}`,
    );
  updateEmployee = (
    id: string,
    data: import("@nimbus-erp/shared").EmployeeUpdateRequest,
  ) =>
    this.request<import("@nimbus-erp/shared").EmployeeDetail>(
      `/organization/employees/${id}`,
      { method: "PUT", body: data },
    );
  resetEmployeePassword = (
    id: string,
    data: import("@nimbus-erp/shared").PasswordResetRequest,
  ) =>
    this.request<void>(`/organization/employees/${id}/reset-password`, {
      method: "POST",
      body: data,
    });
  updateDepartment = (
    id: string,
    data: import("@nimbus-erp/shared").DepartmentUpdateRequest,
  ) =>
    this.request<void>(`/organization/departments/${id}`, {
      method: "PUT",
      body: data,
    });
  openApiCredentials = () =>
    this.request<import("@nimbus-erp/shared").OpenApiCredential[]>(
      "/integration/open-api/credentials",
    );
  createOpenApiCredential = (
    body: import("@nimbus-erp/shared").OpenApiCredentialCreate,
  ) =>
    this.request<import("@nimbus-erp/shared").OpenApiCredential>(
      "/integration/open-api/credentials",
      { method: "POST", body },
    );
  setOpenApiCredentialStatus = (
    id: string,
    body: { version: number; status: "ACTIVE" | "DISABLED"; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").OpenApiCredential>(
      `/integration/open-api/credentials/${id}/status`,
      { method: "PUT", body },
    );
  openApiCallLogs = () =>
    this.request<import("@nimbus-erp/shared").OpenApiCallLog[]>(
      "/integration/open-api/logs",
    );
  webhookSubscriptions = () =>
    this.request<import("@nimbus-erp/shared").WebhookSubscription[]>(
      "/integration/open-api/webhooks",
    );
  createWebhookSubscription = (
    body: import("@nimbus-erp/shared").WebhookCreate,
  ) =>
    this.request<import("@nimbus-erp/shared").WebhookSubscription>(
      "/integration/open-api/webhooks",
      { method: "POST", body },
    );
  setWebhookStatus = (
    id: string,
    body: { version: number; status: "ACTIVE" | "DISABLED"; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").WebhookSubscription>(
      `/integration/open-api/webhooks/${id}/status`,
      { method: "PUT", body },
    );
  webhookDeliveries = () =>
    this.request<import("@nimbus-erp/shared").WebhookDelivery[]>(
      "/integration/open-api/deliveries",
    );
  printTemplates = () =>
    this.request<import("@nimbus-erp/shared").PrintTemplate[]>(
      "/integration/customization/print-templates",
    );
  resolvePrintTemplate = (
    documentType: import("@nimbus-erp/shared").PrintDocumentType,
  ) =>
    this.request<import("@nimbus-erp/shared").PrintTemplate>(
      `/integration/customization/print-templates/resolve?${new URLSearchParams({ documentType })}`,
    );
  savePrintTemplate = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").PrintTemplateSave,
  ) =>
    this.request<import("@nimbus-erp/shared").PrintTemplate>(
      `/integration/customization/print-templates${id ? `/${id}` : ""}`,
      { method: id ? "PUT" : "POST", body },
    );
  dataImportBatches = () =>
    this.request<import("@nimbus-erp/shared").DataImportBatch[]>(
      "/integration/customization/data-imports",
    );
  dataImportBatch = (id: string) =>
    this.request<import("@nimbus-erp/shared").DataImportBatch>(
      `/integration/customization/data-imports/${id}`,
    );
  uploadDataImport = (
    kind: import("@nimbus-erp/shared").DataImportKind,
    file: File,
    idempotencyKey: string,
  ) => {
    const body = new FormData();
    body.append("file", file);
    return this.request<import("@nimbus-erp/shared").DataImportBatch>(
      `/integration/customization/data-imports?${new URLSearchParams({ kind })}`,
      { method: "POST", body, idempotencyKey },
    );
  };
  startDataImportUpload = (
    kind: import("@nimbus-erp/shared").DataImportKind,
    body: {
      fileName: string;
      fileSize: number;
      chunkSize: number;
      totalChunks: number;
    },
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").DataImportUploadSession>(
      `/integration/customization/data-import-uploads?${new URLSearchParams({ kind })}`,
      { method: "POST", body, idempotencyKey },
    );
  dataImportUploadSession = (id: string) =>
    this.request<import("@nimbus-erp/shared").DataImportUploadSession>(
      `/integration/customization/data-import-uploads/${id}`,
    );
  uploadDataImportChunk = (
    id: string,
    index: number,
    chunk: Blob,
    sha256: string,
  ) =>
    this.request<import("@nimbus-erp/shared").DataImportUploadSession>(
      `/integration/customization/data-import-uploads/${id}/chunks/${index}`,
      {
        method: "PUT",
        body: chunk,
        headers: {
          "Content-Type": "application/octet-stream",
          "X-Chunk-SHA256": sha256,
        },
      },
    );
  completeDataImportUpload = (
    id: string,
    version: number,
    fileSha256: string,
  ) =>
    this.request<import("@nimbus-erp/shared").DataImportUploadSession>(
      `/integration/customization/data-import-uploads/${id}/complete`,
      { method: "POST", body: { version, fileSha256 } },
    );
  actOnDataImport = (
    id: string,
    body: { version: number; action: "CONFIRM" | "CANCEL"; reason: string },
  ) =>
    this.request<import("@nimbus-erp/shared").DataImportBatch>(
      `/integration/customization/data-imports/${id}/action`,
      { method: "POST", body },
    );
  downloadDataImportTemplate = async (
    kind: import("@nimbus-erp/shared").DataImportKind,
  ) => {
    const response = await fetch(
      `${this.baseUrl}/integration/customization/data-imports/templates/${kind}`,
      { credentials: "include", headers: { "X-Login-Type": this.loginType } },
    );
    if (!response.ok) {
      const payload = (await response
        .json()
        .catch(() => null)) as ApiEnvelope<unknown> | null;
      throw new ApiError(
        payload?.message ?? `下载失败（HTTP ${response.status}）`,
        response.status,
        payload?.traceId,
      );
    }
    return response.blob();
  };
  downloadDataImportErrors = async (id: string) => {
    const response = await fetch(
      `${this.baseUrl}/integration/customization/data-imports/${id}/errors.csv`,
      { credentials: "include", headers: { "X-Login-Type": this.loginType } },
    );
    if (!response.ok) {
      const payload = (await response
        .json()
        .catch(() => null)) as ApiEnvelope<unknown> | null;
      throw new ApiError(
        payload?.message ?? `下载失败（HTTP ${response.status}）`,
        response.status,
        payload?.traceId,
      );
    }
    return response.blob();
  };
  dataExportJobs = () =>
    this.request<import("@nimbus-erp/shared").DataExportJob[]>(
      "/integration/customization/data-exports",
    );
  requestDataExport = (
    body: import("@nimbus-erp/shared").DataExportRequest,
    idempotencyKey: string,
  ) =>
    this.request<import("@nimbus-erp/shared").DataExportJob>(
      "/integration/customization/data-exports",
      { method: "POST", body, idempotencyKey },
    );
  downloadDataExport = async (id: string) => {
    const response = await fetch(
      `${this.baseUrl}/integration/customization/data-exports/${id}/download`,
      { credentials: "include", headers: { "X-Login-Type": this.loginType } },
    );
    if (!response.ok) {
      const payload = (await response
        .json()
        .catch(() => null)) as ApiEnvelope<unknown> | null;
      throw new ApiError(
        payload?.message ?? `下载失败（HTTP ${response.status}）`,
        response.status,
        payload?.traceId,
      );
    }
    return response.blob();
  };
  tenantManagementDetail = (id: string) =>
    this.request<import("@nimbus-erp/shared").TenantManagementDetail>(
      `/platform/tenants/${id}`,
    );
  tenantAccounts = (id: string) =>
    this.request<import("@nimbus-erp/shared").TenantAccount[]>(
      `/platform/tenants/${id}/accounts`,
    );
  tenantDemoLogin = (id: string) =>
    this.request<{ available: boolean; tenantCode?: string; username?: string; password?: string; message: string }>(
      `/platform/tenants/${id}/demo-login`,
    );
  updateTenant = (
    id: string,
    data: import("@nimbus-erp/shared").TenantUpdateRequest,
  ) =>
    this.request<import("@nimbus-erp/shared").TenantManagementDetail>(
      `/platform/tenants/${id}`,
      { method: "PUT", body: data },
    );
  resetTenantAdministrator = (
    id: string,
    data: import("@nimbus-erp/shared").PasswordResetRequest & {
      userId: string;
    },
  ) =>
    this.request<void>(`/platform/tenants/${id}/reset-administrator`, {
      method: "POST",
      body: data,
    });

  packages = () => this.request<PackageOption[]>("/platform/packages");

  managedPackages = () =>
    this.request<import("@nimbus-erp/shared").ManagedPackage[]>(
      "/platform/package-catalog",
    );
  deletePackage = (id: string, version: number) =>
    this.request<void>(`/platform/package-catalog/${id}?version=${version}`, {
      method: "DELETE",
    });
  packagePermissionCatalog = () =>
    this.request<import("@nimbus-erp/shared").PermissionOption[]>(
      "/platform/package-catalog/permissions",
    );
  createPackage = (data: import("@nimbus-erp/shared").PackageSaveRequest) =>
    this.request<import("@nimbus-erp/shared").ManagedPackage>(
      "/platform/package-catalog",
      { method: "POST", body: data },
    );
  updatePackage = (
    id: string,
    data: import("@nimbus-erp/shared").PackageSaveRequest,
  ) =>
    this.request<import("@nimbus-erp/shared").ManagedPackage>(
      `/platform/package-catalog/${id}`,
      { method: "PUT", body: data },
    );

  tenants = (page = 0, size = 20, search = "", status = "", filters: import("@nimbus-erp/shared").TenantFilters = {}) => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
    });
    if (search) query.set("search", search);
    if (status) query.set("status", status);
    for(const [key,value] of Object.entries(filters)) if(value !== undefined && value !== "") query.set(key,String(value));
    return this.request<PageResult<TenantListItem>>(
      `/platform/tenants?${query}`,
    );
  };
  tenantExpiryOverview = () =>
    this.request<import("@nimbus-erp/shared").ExpiryOverview>(
      "/platform/tenants/expiry-overview",
    );

  platformAuditEvents = (
    page = 0,
    size = 20,
    filters: {
      tenantCode?: string;
      action?: string;
      actorType?: string;
      from?: string;
      to?: string;
    } = {},
  ) => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
    });
    for (const [key, value] of Object.entries(filters)) {
      if (value) query.set(key, value);
    }
    return this.request<PageResult<PlatformAuditEvent>>(
      `/platform/audit-events?${query}`,
    );
  };

  platformOperationsOverview = () =>
    this.request<PlatformOperationsOverview>("/platform/operations/overview");

  platformTenantSignals = (
    page = 0,
    size = 20,
    search = "",
    attentionOnly = true,
  ) => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
      attentionOnly: String(attentionOnly),
    });
    if (search) query.set("search", search);
    return this.request<PageResult<PlatformTenantSignal>>(
      `/platform/operations/tenant-signals?${query}`,
    );
  };

  platformTaskIssues = (page = 0, size = 20, tenantCode = "") => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
    });
    if (tenantCode) query.set("tenantCode", tenantCode);
    return this.request<PageResult<PlatformTaskIssue>>(
      `/platform/operations/task-issues?${query}`,
    );
  };

  createTenant = (input: TenantCreateRequest, idempotencyKey: string) =>
    this.request<TenantListItem>("/platform/tenants", {
      method: "POST",
      body: input,
      idempotencyKey,
    });

  private masterPage<T>(
    resource: string,
    page: number,
    size: number,
    search: string,
    status: string,
  ) {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
      search,
      status,
    });
    return this.request<PageResult<T>>(`/master-data/${resource}?${query}`);
  }

  tradeInvoices = (
    page = 0,
    size = 20,
    search = "",
    direction = "",
    status = "", startDate = "", endDate = "",
  ) =>
    this.request<PageResult<import("@nimbus-erp/shared").TradeInvoice>>(
      `/finance/invoices?${new URLSearchParams({ page: String(page), size: String(size), search, direction, status, ...listDateParams(startDate,endDate) })}`,
    );

  tradeInvoiceSources = (
    direction: import("@nimbus-erp/shared").InvoiceDirection,
    invoiceKind: import("@nimbus-erp/shared").InvoiceKind,
    partnerId: string,
    search = "",
    invoiceDate?: string,
  ) => {
    const query = new URLSearchParams({
      direction,
      invoiceKind,
      partnerId,
      search,
    });
    if (invoiceDate) query.set("invoiceDate", invoiceDate);
    return this.request<import("@nimbus-erp/shared").TradeInvoiceSource[]>(
      `/finance/invoices/sources?${query}`,
    );
  };

  saveTradeInvoice = (
    id: string | undefined,
    body: import("@nimbus-erp/shared").TradeInvoiceDraft,
    key: string,
    version?: number,
  ) =>
    this.request<import("@nimbus-erp/shared").TradeInvoice>(
      `/finance/invoices${id ? `/${id}?version=${version}` : ""}`,
      { method: id ? "PUT" : "POST", body, idempotencyKey: key },
    );

  confirmTradeInvoice = (
    id: string,
    version: number,
    externalCode: string,
    externalNumber: string,
  ) =>
    this.request<import("@nimbus-erp/shared").TradeInvoice>(
      `/finance/invoices/${id}/confirm`,
      { method: "POST", body: { version, externalCode, externalNumber } },
    );

  cancelTradeInvoice = (id: string, version: number, reason: string) =>
    this.request<import("@nimbus-erp/shared").TradeInvoice>(
      `/finance/invoices/${id}/cancel`,
      { method: "POST", body: { version, reason } },
    );

  stockLocationBalances = (
    warehouseId?: string,
    productId?: string,
    page = 0,
    size = 20,
  ) => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
    });
    if (warehouseId) query.set("warehouseId", warehouseId);
    if (productId) query.set("productId", productId);
    return this.request<
      PageResult<import("@nimbus-erp/shared").StockLocationBalance>
    >(`/inventory/locations/balances?${query}`);
  };

  stockLocationOptions = (warehouseId: string, search = "") =>
    this.request<import("@nimbus-erp/shared").StockLocationOption[]>(
      `/inventory/locations/options?${new URLSearchParams({ warehouseId, search })}`,
    );

  stockLocationPickOptions = (
    warehouseId: string,
    productId: string,
    batchNo = "",
  ) =>
    this.request<import("@nimbus-erp/shared").StockLocationPickOption[]>(
      `/inventory/locations/pick-options?${new URLSearchParams({ warehouseId, productId, batchNo })}`,
    );

  stockLocationMoves = (warehouseId?: string, page = 0, size = 20, startDate = "", endDate = "") => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size), ...listDateParams(startDate,endDate),
    });
    if (warehouseId) query.set("warehouseId", warehouseId);
    return this.request<
      PageResult<import("@nimbus-erp/shared").StockLocationMove>
    >(`/inventory/locations/moves?${query}`);
  };

  stockLocationAllocations = (stockLogId: string) =>
    this.request<import("@nimbus-erp/shared").StockLocationAllocation[]>(
      `/inventory/locations/allocations/${stockLogId}`,
    );

  moveStockLocation = (
    body: import("@nimbus-erp/shared").StockLocationMoveRequest,
    key: string,
  ) =>
    this.request<import("@nimbus-erp/shared").StockLocationMove>(
      "/inventory/locations/moves",
      { method: "POST", body, idempotencyKey: key },
    );

  private tradeOrders(
    domain: "sales" | "purchase",
    page: number,
    size: number,
    search: string,
    auditStatus: string,
    businessStatus: string,
    startDate: string,
    endDate: string,
  ) {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
      search,
      auditStatus,
      businessStatus,
    });
    if (startDate) query.set("startDate", startDate);
    if (endDate) query.set("endDate", endDate);
    return this.request<PageResult<TradeOrder>>(`/${domain}/orders?${query}`);
  }
}

const runtimeEnv = (
  import.meta as ImportMeta & { env?: Record<string, string | undefined> }
).env;
export const apiClient = new ApiClient(
  runtimeEnv?.VITE_API_BASE_URL ?? "/api/v1",
);

export const createIdempotencyKey = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `erp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
